import Loading from "@/pages/loading";
import { DashboardRoutes } from "@/router";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { bnplMerchantApis } from "@payping/api";
import type { InstallmentDetail, OrderDetailResponse } from "@payping/api";
import { InstallmentStatus, MerchantOrderSteps } from "@payping/api/app/api/bnplMerchantTypes";
import { Close, CuToman, PhRDownload } from "@payping/icons";
import { bnplMerchantGetOrderDetail } from "@payping/redux/actions";
import { dateFormats, isBeforeToday, moment } from "@payping/utils/dateUtils";
import {
  Alert,
  Button,
  Col,
  Drawer,
  Flex,
  LegacyCard,
  List,
  Modal,
  Popover,
  Progress,
  Row,
  Tag,
  Typography,
  theme,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
  usePaypingContext,
} from "@payping/web-components";
import { FC, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Trans } from "@payping/utils/localeUtils";
import refund from "@/refund/routes/constants";
import BnplListDetailsPageCancelOrderModal from "./cancelOrde";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import BnplListDetailsPageCancelOrderChequeModal from "./cancel-order-cheque";
import RefundCustomerCheque from "./refund-customer-cheque";
import ChequeBackground from "./cheque-background";

interface DetailsProps {
  isOpen: boolean;
  trackingCode: string;
  onClose: () => void;
}

type MyInstallmentDetail = InstallmentDetail & { index: number };

const InstallmentCard: FC<{
  item: MyInstallmentDetail;
  data?: OrderDetailResponse;
  onChangeSalaryStatus: (code: string) => void;
}> = ({ item, data, onChangeSalaryStatus }) => {
  const { token } = theme.useToken();
  const { t } = useAppTranslation();

  const installmentCode = item.code;
  return (
    <LegacyCard.Ant
      title={
        <Flex align="center" gap={"small"}>
          <Progress
            type="circle"
            strokeColor={token.colorPrimary}
            percent={((item.index + 1) * 100) / (data?.installmentCount ?? 1)}
            size={24}
            format={() => null}
          />
          <Typography.Text>
            <strong>
              {t("shop.bnpl.orderDetails.installmentNumber", {
                number: (t("countingNumbers", { returnObjects: true }) as any)[item.index] ?? "",
              })}
            </strong>
          </Typography.Text>
        </Flex>
      }
      type="inner"
      extra={
        <>
          {item.status === InstallmentStatus.Waiting ? (
            <Typography.Text className="tw:text-color-primary">
              {t("shop.bnpl.orderDetails.installmentStatus.waiting")}
            </Typography.Text>
          ) : null}
          {item.status === InstallmentStatus.Delayed ? (
            <Typography.Text type="danger">{t("shop.bnpl.orderDetails.installmentStatus.delayed")}</Typography.Text>
          ) : null}
          {item.status === InstallmentStatus.Defaulted ? (
            <Typography.Text>{t("shop.bnpl.orderDetails.installmentStatus.defaulted")}</Typography.Text>
          ) : null}
          {item.status === InstallmentStatus.PaidOff ? (
            <Typography.Text type="success">{t("shop.bnpl.orderDetails.installmentStatus.paidOff")}</Typography.Text>
          ) : null}
          {item.status === InstallmentStatus.SalaryDeductionWaiting ? (
            <Typography.Text>{t("shop.bnpl.orderDetails.installmentStatus.salaryDeductionWaiting")}</Typography.Text>
          ) : null}
          {item.status === InstallmentStatus.SalaryDeductionPaidOff ? (
            <Typography.Text type="success">
              {t("shop.bnpl.orderDetails.installmentStatus.salaryDeductionPaidOff")}
            </Typography.Text>
          ) : null}
        </>
      }
    >
      <Flex align="center" gap={"large"} wrap>
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{t("common.amount")}</Typography.Text>
          {item.finalAmount && (
            <Flex align="center">
              <Typography.Text>
                {formatLocalizedNumber({ value: item.finalAmount, options: { maximumFractionDigits: 2 } })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
          )}
        </Flex>
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{t("common.dueDate")}</Typography.Text>
          <Typography.Text>
            {item.dueDate ? moment(item.dueDate).format(dateFormats.ui.jalaliDate) : null}
          </Typography.Text>
        </Flex>
        {item.paymentDate ? (
          <Flex align="center" gap={4}>
            <Typography.Text type="secondary">{t("common.paymentDate")}</Typography.Text>
            <Typography.Text>
              {item.paymentDate ? moment(item.paymentDate).format(dateFormats.ui.jalaliDate) : null}
            </Typography.Text>
          </Flex>
        ) : null}

        {item.paymentCode ? (
          <Flex align="center" gap={4}>
            <Typography.Text type="secondary">{t("common.paymentCode")}</Typography.Text>
            <Typography.Text className="tw:dir-ltr">
              <Link to={`${DashboardRoutes.mySale.report.url}?code=${item.paymentCode ?? ""}&searchVariant=code`}>
                {`${item.paymentCode.slice(0, 8)}...`}
              </Link>
            </Typography.Text>
          </Flex>
        ) : null}

        {item.status === InstallmentStatus.SalaryDeductionWaiting && installmentCode ? (
          <Button type="default" className="tw:mr-auto" onClick={() => onChangeSalaryStatus(installmentCode)}>
            {t("shop.bnpl.orderDetails.changeSalaryStatus")}
          </Button>
        ) : null}
      </Flex>
    </LegacyCard.Ant>
  );
};

const BnplListDetailsPage: FC<DetailsProps> = ({ isOpen, onClose, trackingCode }) => {
  const { t } = useAppTranslation();
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const dispatch = useAppDispatch();

  const [changeSalaryStatusModal, setChangeSalaryStatusModal] = useState<{
    installmentCode: string;
    show: boolean;
  }>({
    installmentCode: "",
    show: false,
  });

  const [cancelModal, setCancelModal] = useState(false);
  const [cancelChequeModal, setCancelChequeModal] = useState(false);

  const handleCloseCancelModal = () => {
    setCancelModal(false);
  };
  const handleOpenCancelModal = () => {
    setCancelModal(true);
  };

  const orderDetails = useAppSelector((state) => state.bnplMerchant.orderDetail);

  const getOrderDetailsState = useMessagedAsyncRetry(
    () => dispatch(bnplMerchantGetOrderDetail(trackingCode, undefined, { isRaw: true })),
    [dispatch, trackingCode],
    { error: true, success: false },
  );

  const [chnageSalaryStatusStatus, callChangeSalaryStatus] = useMessagedAsyncFn(
    async (installmentCode: string) => {
      await bnplMerchantApis.setSalaryDeductionPayment({
        trackingCode,
        installmentCode,
      });
      setChangeSalaryStatusModal({ installmentCode: "", show: false });
      await dispatch(bnplMerchantGetOrderDetail(trackingCode, undefined, { isRaw: true }));
    },
    [dispatch, trackingCode],
  );

  const [confirmChequeState, confirmCheque] = useMessagedAsyncFn(async () => {
    await bnplMerchantApis.orderAcceptChequeDelivery({
      trackingCode,
    });
    await dispatch(bnplMerchantGetOrderDetail(trackingCode, undefined, { isRaw: true }));
  }, [dispatch, trackingCode]);

  const [getFileState, getFile] = useMessagedAsyncFn(
    (orderTrackingCode: string, fileName?: string) =>
      bnplMerchantApis.getDownloadPath(fileName, orderTrackingCode, { isRaw: true }),
    [],
  );

  const loading = getOrderDetailsState.loading || getOrderDetailsState.error?.name === "AuthError";

  const guaranteeFileIds = (orderDetails?.guaranteeFileIds ?? [])?.filter((i) => i.length);

  const handleCloseDrawer = () => {
    onClose();
  };

  const handleCloseChangeSalaryStatusModal = () => {
    setChangeSalaryStatusModal({ installmentCode: "", show: false });
  };

  const downloadGuarantee = async (file: string) => {
    try {
      const res = await getFile(trackingCode ?? "", file);
      if (!res.url) return;
      const link = document.createElement("a");
      link.href = res.url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      /* empty */
    }
  };

  const haveAnyWaitingSalary = !!orderDetails?.installmentDetails.find(
    (i) => i.status === InstallmentStatus.SalaryDeductionWaiting,
  );

  if (!trackingCode) {
    return <Navigate to={DashboardRoutes.myShop.bnpl.list.root.url} />;
  }

  const list = (() => {
    const beforeToday: MyInstallmentDetail[] = [];
    const remain: MyInstallmentDetail[] = [];

    orderDetails?.installmentDetails.forEach((item, index) => {
      if (item.dueDate && isBeforeToday(moment.utc(item.dueDate))) {
        beforeToday.push({ ...item, index });
      } else {
        remain.push({ ...item, index });
      }
    });
    return {
      beforeToday,
      remain,
    } as const;
  })();

  const listItems = [
    {
      title: t("shop.bnpl.orderDetails.checkoutAmount"),
      content: (
        <Flex align="center">
          <Typography.Text>
            {orderDetails?.totalBasketAmount
              ? formatLocalizedNumber({ value: orderDetails?.totalBasketAmount, options: { maximumFractionDigits: 2 } })
              : ""}
          </Typography.Text>
          <CuToman width={24} height={24} />
        </Flex>
      ),
    },

    {
      title: t("shop.bnpl.orderDetails.prepayment"),
      content: (
        <Flex align="center" gap={"small"}>
          <Typography.Text type="secondary">
            {`(${(((orderDetails?.prepayment ?? 1) / (orderDetails?.totalBasketAmount ?? 1)) * 100).toFixed(0)}%)`}
          </Typography.Text>
          {orderDetails?.prepayment ? (
            <Flex align="center">
              <Typography.Text>
                {formatLocalizedNumber({ value: orderDetails.prepayment, options: { maximumFractionDigits: 2 } })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
          ) : null}
        </Flex>
      ),
    },
    {
      title: t("shop.bnpl.orderDetails.creditProvided"),
      content: (
        <Flex align="center">
          <Typography.Text>
            {orderDetails?.creditAmount
              ? formatLocalizedNumber({ value: orderDetails?.creditAmount, options: { maximumFractionDigits: 2 } })
              : ""}
          </Typography.Text>
          <CuToman width={24} height={24} />
        </Flex>
      ),
    },
    {
      title: t("shop.bnpl.orderDetails.monthlyProfit"),
      content: `${orderDetails?.installmentInfo.interestRate ?? 0} ${t("common.percent")}`,
    },
    {
      title: t("shop.bnpl.orderDetails.orderCreatedDate"),
      content: `${orderDetails?.orderCreatedDate ? moment(orderDetails?.orderCreatedDate).format(dateFormats.ui.jalaliDate) : null}`,
    },
  ];

  const installmentStatus = (
    <Flex justify="center" gap={"middle"} vertical>
      <Flex align="center" gap={"middle"}>
        <Typography.Title level={5} className="tw:m-0!">
          {t("shop.bnpl.orderDetails.statusTitle")}
        </Typography.Title>
        {haveAnyWaitingSalary ? (
          <Tag> {t("shop.bnpl.orderDetails.orderSteps.salaryDeductionWaiting")}</Tag>
        ) : (
          <>
            {orderDetails?.orderStep === MerchantOrderSteps.Init ? (
              <Tag> {t("shop.bnpl.orderDetails.orderSteps.init")}</Tag>
            ) : null}
            {orderDetails?.orderStep === MerchantOrderSteps.Payment ? (
              <Tag color="processing"> {t("shop.bnpl.orderDetails.orderSteps.payment")}</Tag>
            ) : null}
            {orderDetails?.orderStep === MerchantOrderSteps.PaidOff ? (
              <Tag> {t("shop.bnpl.orderDetails.orderSteps.paidOff")}</Tag>
            ) : null}
            {orderDetails?.orderStep === MerchantOrderSteps.Canceled ? (
              <Tag> {t("shop.bnpl.orderDetails.orderSteps.canceled")}</Tag>
            ) : null}

            {orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundInactive ? (
              <Tag> {t("shop.bnpl.orderDetails.orderSteps.semiCanceledRefundInactive")}</Tag>
            ) : null}

            {orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundBalanceDeficit ? (
              <Tag> {t("shop.bnpl.orderDetails.orderSteps.semiCanceledRefundBalanceDeficit")}</Tag>
            ) : null}

            {orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundInProgress ? (
              <Tag> {t("shop.bnpl.orderDetails.orderSteps.semiCanceledRefundInProgress")}</Tag>
            ) : null}
            {orderDetails?.orderStep === MerchantOrderSteps.Unknown ? (
              <Tag> {t("shop.bnpl.orderDetails.orderSteps.unknown")}</Tag>
            ) : null}
            {orderDetails?.orderStep === MerchantOrderSteps.WaitingForChequeConfirm ? (
              <Tag color="warning">{t("shop.bnpl.ordersList.filters.status.chequeDeliveryAcceptWaiting")}</Tag>
            ) : null}
          </>
        )}
      </Flex>
      {/* 
      {orderDetails?.orderStep === MerchantOrderSteps.Canceled ? (
        <Alert type="error" showIcon title={t("shop.bnpl.orderDetails.orderInfo.canceled")} />
      ) : null} */}

      {orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundBalanceDeficit ? (
        <Alert
          type="warning"
          showIcon
          title={
            <Flex vertical justify="center" gap={"small"}>
              {t("shop.bnpl.orderDetails.orderInfo.semiCanceledRefundBalanceDeficit.label")}
              <Link to={refund.wallet.url}>
                <Button variant="outlined" color="primary">
                  {t("shop.bnpl.orderDetails.orderInfo.semiCanceledRefundBalanceDeficit.action")}
                </Button>
              </Link>
            </Flex>
          }
        />
      ) : null}

      {orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundInProgress ? (
        <Alert type="success" showIcon title={t("shop.bnpl.orderDetails.orderInfo.semiCanceledRefundInProgress")} />
      ) : null}
    </Flex>
  );
  const InstallmentSection = (
    <Flex vertical className="tw:w-full" gap={32}>
      <Flex vertical className="tw:w-full" gap={"middle"}>
        {lg ? installmentStatus : null}
        {orderDetails?.orderStep === MerchantOrderSteps.Canceled ||
        orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundBalanceDeficit ||
        orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundInProgress ||
        orderDetails?.orderStep === MerchantOrderSteps.SemiCanceledRefundInactive ? null : (
          <Flex vertical gap={"middle"}>
            <Flex className="tw:w-full" justify="space-between" align="center">
              <Typography.Title level={5} className="tw:m-0!">
                {t("shop.bnpl.orderDetails.status")}
              </Typography.Title>
              <Typography.Text type="secondary">{`${orderDetails?.installmentInfo.paidInstallmentCount ?? 0}/${orderDetails?.installmentCount ?? 1}`}</Typography.Text>
            </Flex>
            <Progress
              showInfo
              type="line"
              percent={
                ((orderDetails?.installmentInfo.paidInstallmentCount ?? 1) * 100) /
                (orderDetails?.installmentCount ?? 1)
              }
              format={() => null}
            />
            <Flex className="tw:w-full" justify="space-between" align="center">
              <Flex vertical>
                <Flex align="center">
                  <Typography.Text>
                    {formatLocalizedNumber({
                      value: orderDetails?.installmentInfo.paidInstallmentAmount ?? 0,
                      options: { maximumFractionDigits: 2 },
                    })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
                <Typography.Text type="secondary">{t("common.paid")}</Typography.Text>
              </Flex>
              <Flex vertical>
                <Flex align="center">
                  <Typography.Text>
                    {formatLocalizedNumber({
                      value: orderDetails?.installmentInfo.remainedInstallmentAmount ?? 0,
                      options: { maximumFractionDigits: 2 },
                    })}
                  </Typography.Text>
                  <CuToman width={24} height={24} />
                </Flex>
                <Typography.Text type="secondary">{t("common.remain")}</Typography.Text>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
      {orderDetails?.orderStep === MerchantOrderSteps.WaitingForChequeConfirm ? (
        <>
          {cancelChequeModal ? (
            <BnplListDetailsPageCancelOrderChequeModal
              open={cancelChequeModal}
              onCancel={() => setCancelChequeModal(false)}
              onFinish={() => {
                getOrderDetailsState.retry();
              }}
              trackingCode={trackingCode}
            />
          ) : null}
          <LegacyCard.Ant
            type="inner"
            size="default"
            title={
              <Flex justify="center" className="tw:relative tw:py-5!" align="center">
                <Flex className="tw:relative">
                  <ChequeBackground className="tw:relative tw:flex tw:h-auto tw:w-full tw:xs:w-80 tw:md:w-96" />

                  <Flex
                    vertical
                    className="tw:absolute tw:h-full tw:w-full tw:p-2! tw:pl-7! tw:text-[10px]! sm:tw:text-xs! tw:md:p-6! tw:md:pl-10! md:tw:text-sm!"
                  >
                    <Flex justify="space-between" className="tw:mb-auto tw:w-full">
                      <Typography.Text type="secondary">
                        {t("shop.bnpl.orderDetails.chequeInfo.sayyadNumber")}
                      </Typography.Text>
                      <Typography.Text>{orderDetails.chequeInfo?.sayyadNumber ?? ""}</Typography.Text>
                    </Flex>
                    <Flex justify="space-between" className="tw:mb-auto tw:w-full">
                      <Typography.Text type="secondary">
                        {t("shop.bnpl.orderDetails.chequeInfo.chequeDate")}
                      </Typography.Text>
                      <Typography.Text>{orderDetails.chequeInfo?.duedate ?? ""}</Typography.Text>
                    </Flex>
                    <Flex justify="space-between" className="tw:mb-auto tw:w-full">
                      <Typography.Text type="secondary">
                        {t("shop.bnpl.orderDetails.chequeInfo.chequeAmount")}
                      </Typography.Text>
                      <Flex align="center">
                        <Typography.Text>
                          {formatLocalizedNumber({
                            value: (orderDetails?.chequeInfo?.amountRial || 1) / 10,
                            options: { maximumFractionDigits: 2 },
                          })}
                        </Typography.Text>
                        <CuToman className="tw:h-5 tw:w-5 tw:md:h-6! tw:md:w-6!" />
                      </Flex>
                    </Flex>
                    <Flex justify="space-between" className="tw:mb-auto tw:w-full">
                      <Typography.Text type="secondary">
                        {t("shop.bnpl.orderDetails.chequeInfo.inTheName")}
                      </Typography.Text>
                      <Typography.Text>{orderDetails.chequeInfo?.recipientName ?? ""}</Typography.Text>
                    </Flex>
                    <Flex justify="space-between" className="tw:w-full">
                      <Typography.Text type="secondary">
                        {t("shop.bnpl.orderDetails.chequeInfo.recipientIdentifier")}
                      </Typography.Text>
                      <Typography.Text>{orderDetails.chequeInfo?.recipientIdentifier ?? ""}</Typography.Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            }
          >
            <Flex vertical className="tw:w-full" gap={"middle"}>
              <Typography.Title level={5} className="tw:m-0!">
                {t("shop.bnpl.orderDetails.waitingForChequeConfirm.confirmCheque")}
              </Typography.Title>
              <Typography.Paragraph className="tw:m-0!">
                {t("shop.bnpl.orderDetails.waitingForChequeConfirm.confirmChequeDescription")}
              </Typography.Paragraph>
              <Flex justify="end" className="tw:w-full" gap={"small"}>
                <Button
                  loading={confirmChequeState.loading}
                  onClick={() => {
                    confirmCheque();
                  }}
                >
                  {t("common.submit")}
                </Button>
                <Button
                  disabled={confirmChequeState.loading}
                  onClick={() => {
                    setCancelChequeModal(true);
                  }}
                >
                  {t("shop.bnpl.orderDetails.waitingForChequeConfirm.reject.title")}
                </Button>
              </Flex>
            </Flex>
          </LegacyCard.Ant>
        </>
      ) : null}
      {orderDetails?.orderStep === MerchantOrderSteps.Canceled && orderDetails.chequeInfo ? (
        <RefundCustomerCheque
          customerAddress={orderDetails.consumerInfo.address ?? ""}
          customerPhone={orderDetails.consumerInfo.mobileNumber ?? ""}
          customerPostalCode={orderDetails.consumerInfo.postalCode ?? ""}
          customerFullName={`${orderDetails.consumerInfo.firstName} ${orderDetails.consumerInfo.lastName}`}
        />
      ) : null}
      {orderDetails?.orderStep !== MerchantOrderSteps.WaitingForChequeConfirm &&
      !(orderDetails?.orderStep === MerchantOrderSteps.Canceled && orderDetails.chequeInfo) ? (
        <>
          <Typography.Title level={5} className="tw:m-0!">
            {t("shop.bnpl.orderDetails.history")}
          </Typography.Title>
          <Flex vertical className="tw:w-full" gap={"small"}>
            {list.beforeToday.map((item) => (
              <InstallmentCard
                key={item.index}
                item={item}
                data={orderDetails}
                onChangeSalaryStatus={(code) => {
                  setChangeSalaryStatusModal({ installmentCode: code, show: true });
                }}
              />
            ))}
            {list.remain.map((item) => (
              <InstallmentCard
                key={item.index}
                item={item}
                data={orderDetails}
                onChangeSalaryStatus={(code) => {
                  setChangeSalaryStatusModal({ installmentCode: code, show: true });
                }}
              />
            ))}
          </Flex>
        </>
      ) : null}
    </Flex>
  );

  return (
    <>
      <Drawer
        title={t("shop.bnpl.orderDetails.title")}
        open={isOpen}
        placement="bottom"
        size={"100%"}
        closeIcon={false}
        extra={
          <Flex gap={"middle"} align="center">
            {orderDetails?.isCancelable && orderDetails?.orderStep !== MerchantOrderSteps.WaitingForChequeConfirm ? (
              <Popover
                content={
                  <Flex vertical align="center" justify="center" gap={"small"}>
                    <Button onClick={handleOpenCancelModal} type="text">
                      {t("shop.bnpl.orderDetails.actions.cancelOrder.title")}
                    </Button>
                  </Flex>
                }
              >
                <Button type="default">{t("common.operations")}</Button>
              </Popover>
            ) : null}

            <Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleCloseDrawer} />
          </Flex>
        }
      >
        {loading ? (
          <Loading />
        ) : (
          <Row className="tw:w-full">
            {lg ? (
              <Col lg={14} span={24}>
                {InstallmentSection}
              </Col>
            ) : null}

            {lg ? <Col span={0} lg={2}></Col> : null}
            <Col lg={8} span={24}>
              <Flex vertical className="tw:w-full" gap={32}>
                {!lg ? installmentStatus : null}
                <Flex vertical gap={"middle"}>
                  <Typography.Title level={5} className="tw:m-0!">
                    {t("shop.bnpl.orderDetails.buyDetails")}
                  </Typography.Title>
                  <List
                    dataSource={listItems}
                    bordered
                    renderItem={(item) => (
                      <List.Item className="tw:w-full">
                        <Flex className="tw:w-full" align="center" justify="space-between">
                          <Typography.Text type="secondary">{item.title}</Typography.Text>
                          <Typography.Text>{item.content}</Typography.Text>
                        </Flex>
                      </List.Item>
                    )}
                  />
                </Flex>

                {!lg ? InstallmentSection : null}
                <Flex vertical gap={"middle"}>
                  <Typography.Title level={5} className="tw:m-0!">
                    {t("shop.bnpl.orderDetails.clientDetails")}
                  </Typography.Title>
                  <LegacyCard.Ant size="small" className="tw:w-full">
                    <Flex vertical className="tw:w-full" gap={"middle"}>
                      <Flex align="center" justify="space-between" className="tw:w-full">
                        <Typography.Text type="secondary">{t("common.firstName")}</Typography.Text>
                        <Typography.Text>{orderDetails?.consumerInfo.firstName}</Typography.Text>
                      </Flex>
                      <Flex align="center" justify="space-between" className="tw:w-full">
                        <Typography.Text type="secondary">{t("common.lastName")}</Typography.Text>
                        <Typography.Text>{orderDetails?.consumerInfo.lastName}</Typography.Text>
                      </Flex>
                      <Flex align="center" justify="space-between" className="tw:w-full">
                        <Typography.Text type="secondary">{t("common.nationalCode")}</Typography.Text>
                        <Typography.Text>{orderDetails?.consumerInfo.nationalCode || ""}</Typography.Text>
                      </Flex>
                      <Flex align="center" justify="space-between" className="tw:w-full">
                        <Typography.Text type="secondary">{t("common.birthDate")}</Typography.Text>
                        <Typography.Text>
                          {orderDetails?.consumerInfo.birthDate
                            ? moment(orderDetails?.consumerInfo.birthDate).format(dateFormats.ui.jalaliDate)
                            : ""}
                        </Typography.Text>
                      </Flex>
                      <Flex align="center" justify="space-between" className="tw:w-full">
                        <Typography.Text type="secondary">{t("common.mobileNumber")}</Typography.Text>
                        <Typography.Text>{orderDetails?.consumerInfo.mobileNumber || ""}</Typography.Text>
                      </Flex>
                      <Flex align="center" justify="space-between" className="tw:w-full">
                        <Typography.Text type="secondary">{t("shop.bnpl.creditScoreInBuyTime")}</Typography.Text>
                        <Typography.Text>{orderDetails?.creditScoreDescription || 0}</Typography.Text>
                      </Flex>
                      <Flex align="center" justify="space-between" className="tw:w-full">
                        <Typography.Text type="secondary">{t("shop.bnpl.orderTrackingCode")}</Typography.Text>
                        <Typography.Text className="tw:break-all">{orderDetails?.trackingCode || ""}</Typography.Text>
                      </Flex>
                    </Flex>
                  </LegacyCard.Ant>
                </Flex>
                {guaranteeFileIds?.length ? (
                  <Flex vertical gap={"middle"}>
                    <Typography.Title level={5} className="tw:m-0!">
                      {t("shop.bnpl.orderDetails.documents")}
                    </Typography.Title>
                    {guaranteeFileIds?.map((item) => {
                      if (!item) return null;
                      return (
                        <Flex align="center" justify="space-between" key={item}>
                          <Typography.Text>
                            <strong>{t("shop.bnpl.orderDetails.downloadGuarantee")}</strong>
                          </Typography.Text>
                          <Button
                            loading={getFileState.loading}
                            onClick={async () => {
                              await downloadGuarantee(item);
                            }}
                            icon={<PhRDownload width={16} />}
                          ></Button>
                        </Flex>
                      );
                    })}
                  </Flex>
                ) : null}
              </Flex>
            </Col>
          </Row>
        )}
      </Drawer>
      {changeSalaryStatusModal.show ? (
        <Modal
          open={changeSalaryStatusModal.show}
          onCancel={() => {
            handleCloseChangeSalaryStatusModal();
          }}
          title={t("shop.bnpl.orderDetails.changeSalaryStatusModal.title")}
          closable
          footer={
            <Flex className="tw:w-full" align="center" justify="end" gap={"middle"}>
              <Button
                onClick={() => {
                  handleCloseChangeSalaryStatusModal();
                }}
                type="default"
                disabled={chnageSalaryStatusStatus.loading}
              >
                {t("shop.bnpl.orderDetails.changeSalaryStatusModal.cancel")}
              </Button>
              <Button
                loading={chnageSalaryStatusStatus.loading}
                onClick={() => callChangeSalaryStatus(changeSalaryStatusModal.installmentCode)}
                type="primary"
              >
                {t("shop.bnpl.orderDetails.changeSalaryStatusModal.submit")}
              </Button>
            </Flex>
          }
        >
          <Trans
            i18nKey={"shop.bnpl.orderDetails.changeSalaryStatusModal.content"}
            components={{
              strong: <strong />,
            }}
          />
        </Modal>
      ) : null}
      {orderDetails?.orderStep !== MerchantOrderSteps.WaitingForChequeConfirm ? (
        <BnplListDetailsPageCancelOrderModal
          open={cancelModal}
          onCancle={handleCloseCancelModal}
          trackingCode={trackingCode}
          onFinish={() => {
            getOrderDetailsState.retry();
            handleCloseCancelModal();
          }}
        />
      ) : null}
    </>
  );
};

export default BnplListDetailsPage;
